/** Descriptor for a single UI palette (the shape of `appearance.palettes` entries). */
export interface ThemePalette {
  /** CSS class id applied to `<body>` (e.g. `'theme-neutral'`). */
  id: string;
  /** Human-readable label shown in the palette selector UI. */
  label: string;
  /**
   * Optional stylesheet URL (e.g. a Google Fonts link) to load when this palette is active.
   * When present, `ThemePaletteService` injects a `<link rel="stylesheet">` into `<head>`
   * exactly once (idempotent). Palettes without this field load no additional stylesheet.
   */
  fontHref?: string;
}

/**
 * Compile-time constants for the authentication strategy options.
 * Follows the const-object-then-extract pattern to keep a single source of truth.
 */
export const AUTH_STRATEGY = {
  LOCAL: 'local',
  AUTHORIZATION_CODE: 'authorization-code',
} as const;

export type AuthStrategy = (typeof AUTH_STRATEGY)[keyof typeof AUTH_STRATEGY];

/**
 * Shape of the `environment` object imported via `environments/environment`.
 * All environment files must satisfy this interface.
 */
export interface Environment {
  production: boolean;
  debug: boolean;
  /**
   * Authentication strategy for this deployment.
   * - 'local': this app's own backend is the authorization server; the SPA is a public client
   *   (authorization code + PKCE, no secret) that signs users in through its own login form.
   * - 'authorization-code': satellite mode — delegates ALL auth to a remote hub
   *   via OAuth Authorization Code; no local login UI is shown.
   */
  authStrategy: AuthStrategy;
  api: {
    graphql: string;
    rest: string;
  };
  /**
   * Build-time language configuration for this deployment (design D3 of
   * `centralize-langs`). Resolution happens once, in `LangService.panel` —
   * consumers never repeat it.
   */
  lang: {
    /**
     * Principal DATA language. Used only to decide which translation is the
     * "main" one for i18n content (the non-deletable, always-editable
     * locale) — it does NOT intervene in the panel (switcher, Account form
     * selector) or in the Transloco `defaultLang`/`fallbackLang`. Usually
     * coincides with `fallback`, but they are distinct concepts: `base` is
     * about DATA, `fallback` is about the FRONTEND UI.
     */
    base: string;
    /**
     * Default language of the FRONTEND: Transloco `defaultLang`, the
     * fallback used when a key is missing, and the language shown in
     * contexts with no resolvable preference (e.g. the pre-auth login
     * screen). Usually coincides with `base`, but they are distinct
     * concepts — see `base`.
     */
    fallback: string;
    /**
     * Universe of languages this installation serves: drives the panel
     * switcher (toolbar) and the data-translation management flags. Every
     * ISO 639-2 code listed here MUST have a matching
     * `public/i18n/<iso6392>.json` file — the panel resolves this array
     * against the languages catalog (`LangService.panel`) by `iso6392`,
     * WITHOUT gating on the catalog's `isActive`; the ORDER in this array
     * (not the catalog's `sort`) decides the panel order.
     */
    langs: string[];
  };
  /**
   * Calendar behaviour of the date pickers (`au-date-picker`, `au-datetime-picker`).
   * The TEXTS of the calendar follow the active panel language; what is declared
   * here is a per-project decision the language does not make.
   */
  calendar: {
    /**
     * First day of the week shown by the calendar: 0 (Sunday) to 6 (Saturday).
     * Declared per project — the panel language never decides it, so a Spanish
     * panel and an English panel of the same project start the week on the same day.
     */
    weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  };
  oAuth: {
    /** The SPA's `client_id`. A public client declares no secret. */
    applicationCode: string;
  };
  appearance?: {
    /**
     * The installation's brand, shown in the sidebar header and the browser tab.
     * `logo` and `favicon` are URLs relative to the app root; place the files in
     * `frontend/public/assets/brand/` (e.g. `assets/brand/logo.svg`).
     */
    brand: {
      /** Product name: sidebar title and browser tab title. */
      name: string;
      /** Secondary line under the name in the sidebar; omitted when absent. */
      subtitle?: string;
      /** Sidebar logo image; without it the default icon box is shown. */
      logo?: string;
      /** Browser tab icon; without it `favicon.ico` stays. */
      favicon?: string;
    };
    /** Class id of the default theme palette (e.g. `'theme-neutral'`). */
    theme: string;
    layout: string;
    /** Whether the runtime palette selector is shown in the header (dev on, prod off). */
    themeSelector: boolean;
    /** Ordered list of available palettes shown in the runtime palette selector. */
    palettes: ThemePalette[];
  };
  msEntraId?: {
    tenant: string;
    authority: string;
    clientId: string;
    redirectUri: string;
    scopes: string[];
  };
}
